import React, { Component } from 'react';
import { renderHighlightIcon } from './utils/highlightIcons';

/**
 * Mobile-web variant of the vehicle highlights overlay.
 *
 * Same data as the desktop view, different UI: instead of the six pills stacked on
 * the right, mweb shows ONE card at a time, centered at the bottom, that changes
 * with the car angle as the 360 spin progresses.
 *
 * The total spin frames are split into N equal slices (one per spec item) so the
 * current frame picks the active card. The card stays visible at rest; whenever
 * the active item changes it fades out, swaps its data, then fades back in.
 */
const FADE_MS = 500;
const MIN_DWELL_MS = 1200; // Minimum time a card stays before it may change again

class MwebResponsiveVehicleHighlights extends Component {
  static CIRCLE_SIZE = 38; // Icon badge diameter
  static OUTER_RADIUS = 18;
  static INNER_RADIUS = 14;

  constructor(props) {
    super(props);
    this.state = {
      displayIndex: this.getActiveIndex(),
      opacity: 1,
      rotateX: 0, // Top-to-bottom flip angle for the content swap
      animate: true, // Toggled off for the instant pre-enter reset
    };
    this.fadeTimeout = null;
    this.dwellTimeout = null;
    this.isFading = false;
    this.lastSwapAt = Date.now();
  }

  componentDidUpdate() {
    this.maybeSwap();
  }

  componentWillUnmount() {
    clearTimeout(this.fadeTimeout);
    clearTimeout(this.dwellTimeout);
    cancelAnimationFrame(this.rafId);
  }

  // Start a fade only once the current card has been shown for MIN_DWELL_MS, so
  // fast spins don't flip the data rapidly - swaps are throttled to one per dwell.
  maybeSwap = () => {
    if (this.isFading) return;
    if (this.getActiveIndex() === this.state.displayIndex) return;

    const wait = Math.max(0, MIN_DWELL_MS - (Date.now() - this.lastSwapAt));
    clearTimeout(this.dwellTimeout);
    if (wait === 0) {
      this.startFade();
    } else {
      this.dwellTimeout = setTimeout(this.startFade, wait);
    }
  };

  startFade = () => {
    // Re-check against the latest frame in case the spin moved during the dwell.
    if (this.getActiveIndex() === this.state.displayIndex) return;
    this.isFading = true;
    // Flip the card edge-on (top-to-bottom) - stays opaque, no fade.
    this.setState({ rotateX: 90, animate: true });
    clearTimeout(this.fadeTimeout);
    this.fadeTimeout = setTimeout(this.swapAndFadeIn, FADE_MS);
  };

  // At the edge-on point, swap the content (instantly to the other side) then flip back.
  swapAndFadeIn = () => {
    this.isFading = false;
    this.lastSwapAt = Date.now();
    // Instantly (no transition) place the new content pre-flipped to the other side...
    this.setState(
      { displayIndex: this.getActiveIndex(), rotateX: -90, animate: false },
      () => {
        cancelAnimationFrame(this.rafId);
        // ...then on the next frames, enable the transition and flip it into place.
        this.rafId = requestAnimationFrame(() => {
          this.rafId = requestAnimationFrame(() =>
            this.setState({ rotateX: 0, animate: true })
          );
        });
        // The spin may have advanced further during the flip; re-evaluate (throttled).
        this.maybeSwap();
      }
    );
  };

  // Which spec item the current spin frame maps to (equal slices across the spin).
  getActiveIndex = () => {
    const { currentFrame = 0, totalFrames = 0, specItemsConfig = [] } = this.props;
    const numItems = Math.min(specItemsConfig.length, 6);
    if (numItems === 0) return 0;
    const sliceSize = (totalFrames || numItems) / numItems;
    const idx = Math.floor(currentFrame / sliceSize);
    return Math.max(0, Math.min(idx, numItems - 1));
  };

  render() {
    const {
      vehicleData = {},
      specItemsConfig = [],
      debug = false,
    } = this.props;

    if (!specItemsConfig || specItemsConfig.length === 0) {
      return null;
    }

    const { displayIndex, opacity, rotateX, animate } = this.state;
    const numItems = Math.min(specItemsConfig.length, 6);
    const activeIndex = Math.max(0, Math.min(displayIndex, numItems - 1));

    const item = specItemsConfig[activeIndex];
    const rawValue = vehicleData?.[item.key] || item.value || '';
    const fullValue = String(rawValue);
    const label = item.label;

    const make  = vehicleData?.make        || '';
    const model = vehicleData?.modelName   || '';
    const trim  = vehicleData?.trim        || '';
    const year  = vehicleData?.year        || '';

    const CIRCLE_SIZE = MwebResponsiveVehicleHighlights.CIRCLE_SIZE;
    const OUTER_RADIUS = MwebResponsiveVehicleHighlights.OUTER_RADIUS;
    const INNER_RADIUS = MwebResponsiveVehicleHighlights.INNER_RADIUS;
    const center = CIRCLE_SIZE / 2;

    return (
      <>
        <style>{`
          @keyframes mwebHeadingSlideInLeft {
            from { opacity: 0; transform: translateX(-40px); }
            to   { opacity: 1; transform: translateX(0); }
          }
        `}</style>

        {make && (
          <div
            style={{
              position: 'absolute',
              top: '14px',
              left: '14px',
              pointerEvents: 'none',
              zIndex: 11,
              userSelect: 'none',
              WebkitUserSelect: 'none',
              maxWidth: '60%',
              textAlign: 'left',
              animation: 'mwebHeadingSlideInLeft 0.55s cubic-bezier(0.34, 1.56, 0.64, 1) both',
            }}
          >
            <div
              style={{
                fontSize: '16px',
                fontWeight: 700,
                color: '#FFFFFF',
                fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif",
                letterSpacing: '0.5px',
                textShadow: '2px 2px 8px rgba(0,0,0,0.5)',
                lineHeight: 1.1,
                textTransform: 'uppercase',
              }}
            >
              {make}
            </div>
            <div
              style={{
                fontSize: '9px',
                fontWeight: 400,
                color: '#FFFFFF',
                fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif",
                letterSpacing: '0.5px',
                textShadow: '1px 1px 6px rgba(0,0,0,0.5)',
                lineHeight: 1.2,
                marginTop: '2px',
                textTransform: 'uppercase',
              }}
            >
              {`${model} ${trim} ${year}`.trim()}
            </div>
          </div>
        )}

        <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: '10px',
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'flex-end',
          pointerEvents: 'none',
          zIndex: 10,
          padding: '0 16px',
          perspective: '700px',
          userSelect: 'none',
          WebkitUserSelect: 'none',
        }}
      >
        {/* Single card - content swaps with a smooth top-to-bottom flip */}
        <div
          style={{
            position: 'relative',
            display: 'inline-flex',
            alignItems: 'center',
            minHeight: '27px',
            maxWidth: '100%',
            paddingLeft: `${CIRCLE_SIZE + 10}px`,
            paddingRight: '15px',
            background: 'rgba(255, 255, 255, 0.55)',
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
            // border: '1px solid rgba(255, 255, 255, 0.6)',
            borderRadius: '9999px',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
            transform: `rotateX(${rotateX}deg)`,
            transformOrigin: 'center',
            backfaceVisibility: 'hidden',
            willChange: 'transform',
            transition: animate ? 'transform 0.55s cubic-bezier(0.25, 0.46, 0.45, 0.94)' : 'none',
          }}
        >
          {/* Icon badge - overlaps the left edge and pops slightly above/below */}
          <div
            style={{
              position: 'absolute',
              left: '-6px',
              top: '60%',
              transform: 'translateY(-60%)',
              width: `${CIRCLE_SIZE}px`,
              height: `${CIRCLE_SIZE}px`,
            }}
          >
            <svg
              width={CIRCLE_SIZE}
              height={CIRCLE_SIZE}
              viewBox={`0 0 ${CIRCLE_SIZE} ${CIRCLE_SIZE}`}
              style={{ overflow: 'visible', display: 'block' }}
            >
              <defs>
                <linearGradient id="mwebCircleGradient" x1="50%" y1="0%" x2="50%" y2="100%">
                  <stop offset="0" stopColor="white" />
                  <stop offset="1" stopColor="#999999" />
                </linearGradient>
                <linearGradient id="mwebCircleBorderGradient" x1="0%" y1="50%" x2="100%" y2="50%">
                  <stop offset="0" stopColor="#2D70D4" stopOpacity="0" />
                  <stop offset="1" stopColor="white" />
                </linearGradient>
                <filter id="mwebCircleShadow" x="-50%" y="-50%" width="200%" height="200%">
                  <feDropShadow dx="-3" dy="5" stdDeviation="3" floodColor="#1d1800" floodOpacity="0.3" />
                </filter>
              </defs>

              {/* Outer circle */}
              <circle
                cx={center}
                cy={center}
                r={OUTER_RADIUS}
                fill="url(#mwebCircleGradient)"
                stroke="url(#mwebCircleBorderGradient)"
                strokeWidth="1.04"
                filter="url(#mwebCircleShadow)"
              />
              {/* Inner circle */}
              <circle
                cx={center}
                cy={center}
                r={INNER_RADIUS}
                fill="url(#mwebCircleGradient)"
                stroke="url(#mwebCircleBorderGradient)"
                strokeWidth="1.04"
              />
              {/* Icon */}
              {renderHighlightIcon({
                label,
                circleX: center,
                circleY: center,
                innerRadius: INNER_RADIUS,
              })}
            </svg>
          </div>

          {/* Label */}
          <span
            style={{
              fontSize: '12px',
              fontWeight: 400,
              color: '#1a1a1a',
              fontFamily: "'Mona Sans', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
              whiteSpace: 'nowrap',
            }}
          >
            {label}
          </span>

          {/* Separator */}
          <span
            style={{
              width: '1px',
              height: '20px',
              background: 'rgba(0, 0, 0, 0.18)',
              margin: '0 11px',
              flexShrink: 0,
            }}
          />

          {/* Value */}
          <span
            title={fullValue}
            style={{
              fontSize: '13px',
              fontWeight: 700,
              color: '#000000',
              fontFamily: "'Mona Sans', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {fullValue || '(no value)'}
          </span>

          {/* Debug info */}
          {debug && (
            <span
              style={{
                position: 'absolute',
                top: '-18px',
                left: '0',
                fontSize: '10px',
                color: '#ff0000',
                fontFamily: 'monospace',
                whiteSpace: 'nowrap',
              }}
            >
              {activeIndex + 1}/{numItems} · f{this.props.currentFrame}/{this.props.totalFrames} · o{opacity.toFixed(2)}
            </span>
          )}
        </div>
      </div>
      </>
    );
  }
}

export default MwebResponsiveVehicleHighlights;
